import { Bar, ComposedChart, CartesianGrid, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { TrendGranularity, TrendPoint } from "@/services/wholesale-analytics";
import { fmtKm } from "@/lib/wholesale-format";
import { fmtBucket } from "@/lib/wholesale-analytics-period";

const COMPACT = new Intl.NumberFormat("bs-BA", { notation: "compact", maximumFractionDigits: 1 });
/** Prethodni period je siva isprekidana linija u oba moda; tekući ide bojom teksta (`currentColor`). */
const PREVIOUS = "#9ca3af";

const GRANULARITY_LABEL: Record<TrendGranularity, string> = { day: "po danu", week: "po sedmici", month: "po mjesecu" };

/** Promet kroz period: tekući period kao stupci (svaki je zbir jednog dana/sedmice/mjeseca, pa
 *  glatka kriva između njih ne bi bila istinita), prethodni poravnat po korpi kao isprekidana linija. */
export function TrendChart({ points, granularity, compare }: { points: TrendPoint[]; granularity: TrendGranularity; compare: boolean }) {
  const data = points.map((p) => ({ ...p, label: fmtBucket(p.start, granularity) }));
  return (
    <div className="px-3 pt-3 pb-2">
      <div className="flex items-center gap-4 text-[11px] text-muted-foreground mb-2">
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 bg-foreground/80" />Ovaj period</span>
        {compare && (
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0 w-4 border-t border-dashed" style={{ borderColor: PREVIOUS }} />Prethodni period
          </span>
        )}
        <span className="ml-auto">{GRANULARITY_LABEL[granularity]}</span>
      </div>
      <div className="h-56 text-foreground">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.08} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: "currentColor", fillOpacity: 0.55 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "currentColor", fillOpacity: 0.55 }}
              tickLine={false}
              axisLine={false}
              width={44}
              tickFormatter={(v) => COMPACT.format(Number(v))}
            />
            <Tooltip
              cursor={{ fill: "currentColor", fillOpacity: 0.05 }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0]!.payload as TrendPoint;
                return (
                  <div className="bg-popover text-popover-foreground ring-1 ring-foreground/10 px-2.5 py-1.5 text-xs shadow-sm">
                    <div className="text-muted-foreground mb-0.5">{fmtBucket(p.start, granularity, true)}</div>
                    <div className="tabular-nums font-medium">{fmtKm(p.current)}</div>
                    {compare && <div className="tabular-nums text-muted-foreground">prije: {fmtKm(p.previous)}</div>}
                  </div>
                );
              }}
            />
            <Bar dataKey="current" fill="currentColor" fillOpacity={0.82} maxBarSize={36} isAnimationActive={false} />
            {compare && <Line
              type="linear"
              dataKey="previous"
              stroke={PREVIOUS}
              strokeWidth={1.25}
              strokeDasharray="4 3"
              dot={{ r: 1.5, fill: PREVIOUS, strokeWidth: 0 }}
              activeDot={{ r: 3, fill: PREVIOUS, strokeWidth: 0 }}
              isAnimationActive={false}
            />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
